import { useFormContext, useFormState, Controller, useFieldArray } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/AddRounded';
import { Input } from '@components/Input';
import Button from '@components/Button';
import { Select } from '@components/Select';
import PhoneInput from '@components/PhoneInput';
import FieldWithActions, { InlineFieldsRow, AddRowButton, rowDeleteAction } from '@components/FieldWithActions';
import { useTranslation } from '@hooks';
import { ChatType, MAX_ADDITIONAL_PHONES } from '@constants/companies';
import { isPersistedPhoneInput, setInitialPhoneNumberToForm } from '@helpers/phone';

export const Form = styled('div')({
    maxWidth: 698,
    display: 'grid',
    columnGap: 24,
    rowGap: 16,
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 'none',
    },
});

const StyledPhoneInput = styled(PhoneInput)({
    paddingBottom: 16,
    '& .error-message': {
        bottom: -14,
    },
});

const PhoneNumbers = styled('div')({
    display: 'grid',
    alignContent: 'start',
});

const AdditionalPhoneRow = styled(FieldWithActions)({
    marginBottom: 16,
    '& > :first-child': {
        paddingBottom: 0,
    },
});

const Contacts = ({ accountLocale }) => {
    const { t } = useTranslation();
    const { control, getValues, setValue, trigger, watch } = useFormContext();
    const { isSubmitting } = useFormState({ control });
    const {
        fields: additionalPhones,
        append: appendAdditionalPhone,
        remove: removeAdditionalPhone,
    } = useFieldArray({
        control,
        name: 'additionalPhones',
    });
    const revalidatePhones = editedName =>
        trigger(
            [
                'phoneNumber',
                ...getValues('additionalPhones').map(({ number }, index) =>
                    isPersistedPhoneInput(number) ? `additionalPhones.${index}.number` : null,
                ),
                editedName,
            ].filter(Boolean),
        );
    const setPhone = (name, value) => {
        setValue(name, value, { shouldDirty: true });
        revalidatePhones(name);
    };
    const watchChatLinkAdded = watch('chatLinkAdded');

    return (
        <>
            <Form>
                <PhoneNumbers>
                    <div style={{ display: 'grid', gridGap: '8px', gridTemplateColumns: 'calc(100% - 73px) 65px' }}>
                        <Controller
                            control={control}
                            name="phoneNumber"
                            render={({ field, fieldState: { error } }) => (
                                <StyledPhoneInput
                                    placeholder=""
                                    label={t('SettingsCompanies.primaryPhone')}
                                    inputProps={{ name: 'phoneNumber' }}
                                    value={field.value}
                                    autoFormat
                                    onChange={value => setPhone('phoneNumber', value)}
                                    error={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="phoneExtension"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.phoneExtension')}
                                    fullWidth
                                    error={!!error}
                                />
                            )}
                        />
                    </div>
                    {additionalPhones.map((field, index) => (
                        <AdditionalPhoneRow
                            key={field.id}
                            actions={rowDeleteAction({
                                onDelete: () => {
                                    removeAdditionalPhone(index);
                                    revalidatePhones();
                                },
                                disabled: isSubmitting,
                                t,
                            })}
                        >
                            <Controller
                                control={control}
                                name={`additionalPhones[${index}].number`}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledPhoneInput
                                        placeholder=""
                                        label={t('SettingsCompanies.additionalPhone')}
                                        value={field.value}
                                        autoFormat
                                        onChange={value => setPhone(`additionalPhones[${index}].number`, value)}
                                        error={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />
                        </AdditionalPhoneRow>
                    ))}
                    {additionalPhones.length < MAX_ADDITIONAL_PHONES && (
                        <AddRowButton
                            label={t('SettingsCompanies.addPhone')}
                            disabled={isSubmitting}
                            onClick={() =>
                                appendAdditionalPhone({ number: setInitialPhoneNumberToForm('', accountLocale) })
                            }
                        />
                    )}
                </PhoneNumbers>
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.email')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="website"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.website')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                {watchChatLinkAdded && (
                    <InlineFieldsRow
                        actions={rowDeleteAction({
                            onDelete: () => {
                                setValue('chatLinkAdded', false, { shouldValidate: true });
                                setValue('chatLink', setInitialPhoneNumberToForm('', accountLocale), {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                setValue('chatType', ChatType.SMS, { shouldDirty: true });
                            },
                            t,
                        })}
                    >
                        <Controller
                            control={control}
                            name="chatLink"
                            render={({ field, fieldState: { error } }) => (
                                <PhoneInput
                                    placeholder=""
                                    label={t('common.phoneNumber')}
                                    value={field.value}
                                    autoFormat
                                    onChange={value => {
                                        setValue('chatLink', value, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    error={error ? t(error.message) : null}
                                    required={watchChatLinkAdded}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="chatType"
                            render={({ field, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) =>
                                        setValue('chatType', value, { shouldDirty: true })
                                    }
                                    label={t('common.type')}
                                    fullWidth
                                    options={[
                                        { label: 'SMS', value: ChatType.SMS },
                                        { label: 'WhatsApp', value: ChatType.WhatsApp },
                                    ]}
                                />
                            )}
                        />
                    </InlineFieldsRow>
                )}
            </Form>
            <Button
                variant="outlined"
                sx={{ width: 'max-content', marginTop: '8px' }}
                startIcon={<AddIcon />}
                disabled={watchChatLinkAdded}
                onClick={() => setValue('chatLinkAdded', true, { shouldValidate: true })}
            >
                {t('SettingsCompanies.addChat')}
            </Button>
        </>
    );
};

export default Contacts;
